<template>
  <div
    class="flex bg-layout-container h-screen pt-[var(--impersonation-banner-height,0px)]"
  >
    <!-- Sidebar - Only render if sidebar slot has content -->
    <aside
      v-if="$slots.sidebar"
      ref="sidebarRef"
      :class="sidebarClasses"
      :style="sidebarStyles"
      role="navigation"
      :aria-label="ariaLabel"
    >
      <slot name="sidebar" />
    </aside>

    <!-- Main Content Area -->
    <div
      class="flex-1 flex flex-col min-w-0 bg-layout-container main-content-area"
      :class="mainContentClasses"
      :style="mainContentStyles"
    >
      <!-- PrintIQ KPI Top Bar - always visible; on small screens shows only page header (center OTS hidden in AppTopBarV2) -->
      <div v-if="$slots.sidebar">
        <AppTopBarV2 v-if="USE_TOP_BAR_V2" />
        <PrintIqKpiTopBar v-else />
      </div>

      <!-- Main Content -->
      <BaseScrollbar
        ref="mainScrollbarRef"
        :horizontal="true"
        :vertical="true"
        :options="{
          wheelSpeed: 1,
          wheelPropagation: false,
          swipeEasing: true,
          suppressScrollX: false,
          suppressScrollY: false
        }"
        class="flex-1 bg-transparent"
      >
        <!--
          Size main/page by content (min-h-full only), not flex-1/min-h-0.
          flex-1 + min-h-0 locks height to the scrollport so tall pages (e.g. Trends)
          clip and the main BaseScrollbar never gains a vertical range.
        -->
        <main class="w-full min-h-full flex flex-col">
          <div
            class="page-content-full-width w-full min-h-full flex flex-col"
            :class="pageContentWrapperClasses"
          >
            <slot />
          </div>
        </main>
      </BaseScrollbar>

      <!-- Sticky horizontal scroll bar (when a table has horizontal overflow) -->
      <StickyHorizontalScrollBar />
      <!-- Fixed bottom pagination bar (when a table view has registered) -->
      <BottomPaginationBar />
    </div>

    <!-- Mobile Overlay - Only if sidebar exists -->
    <div
      v-if="$slots.sidebar && isMobile && isOpen"
      class="fixed inset-0 bg-overlay z-40 lg:hidden"
      @click="closeSidebar"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch, onMounted, onUnmounted, nextTick, inject } from 'vue'
import { useRoute } from 'vue-router'
import BaseScrollbar from '@/components/layout/BaseScrollbar.vue'
import AppTopBarV2 from '@/components/layout/AppTopBarV2.vue'
import PrintIqKpiTopBar from '@/components/layout/PrintIqKpiTopBar.vue'
import BottomPaginationBar from '@/components/layout/BottomPaginationBar.vue'
import StickyHorizontalScrollBar from '@/components/layout/StickyHorizontalScrollBar.vue'
import { useBottomPagination } from '@/composables/useBottomPagination'

const USE_TOP_BAR_V2 = true
const { visible: bottomPaginationVisible } = useBottomPagination()

interface Props {
  collapsed?: boolean
  modelValue?: boolean
  breakpoint?: 'md' | 'lg' | 'xl'
  sidebarWidth?: number | string
  collapsedWidth?: number | string
  persistState?: boolean
  storageKey?: string
  ariaLabel?: string
  showToggle?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  modelValue: false,
  breakpoint: 'lg',
  sidebarWidth: 256,
  collapsedWidth: 64,
  persistState: true,
  storageKey: 'rukh:layout',
  ariaLabel: 'Primary navigation',
  showToggle: true
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  toggle: []
  'collapse-change': [collapsed: boolean]
}>()


// Refs
const sidebarRef = ref<HTMLElement>()
const mainScrollbarRef = ref<InstanceType<typeof BaseScrollbar> | null>(null)
const isOpen = ref(props.modelValue)
const isCollapsed = ref(props.collapsed)
const isMobile = ref(false)
const route = useRoute()

/** Allow child views (e.g. Products) to request main scroll area re-measure after content changes (e.g. after filtering). */
function requestScrollbarUpdate() {
  nextTick(() => {
    mainScrollbarRef.value?.forceUpdate?.()
  })
}
provide('layoutScrollbarUpdate', requestScrollbarUpdate)

// Computed properties
const sidebarClasses = computed(() => {
  if (isMobile.value) {
    // Mobile: off-canvas overlay with fixed 320px width
    return [
      'flex flex-col',
      'fixed left-0 top-[var(--impersonation-banner-height,0px)] bottom-0 z-50 transform transition-transform duration-300 ease-in-out',
      isOpen.value ? 'translate-x-0' : '-translate-x-full',
      'w-[320px] min-w-[320px]' // Fixed 320px width
    ].join(' ')
  } else {
    // Desktop: docked sidebar; width/minWidth come from sidebarStyles (320px or 72px when collapsed)
    return [
      'hidden lg:flex lg:fixed lg:top-[var(--impersonation-banner-height,0px)] lg:bottom-0 lg:flex-col',
      'flex-col'
    ].join(' ')
  }
})

const sidebarStyles = computed(() => {
  if (isMobile.value) {
    // Mobile: fixed 320px width (no transition for overlay)
    return {
      width: '320px',
      minWidth: '320px'
    }
  }

  // Desktop: smooth width transition (150ms)
  const base = {
    transition: 'width 0.15s ease'
  }
  if (isCollapsed.value) {
    return {
      ...base,
      width: `${props.collapsedWidth}px`,
      minWidth: `${props.collapsedWidth}px`
    }
  }
  return {
    ...base,
    width: `${props.sidebarWidth}px`,
    minWidth: `${props.sidebarWidth}px`
  }
})

const mainContentClasses = computed(() => {
  if (isMobile.value) {
    return ''
  }
  // Desktop: transition is in mainContentStyles (inline) so padding-left animates
  return ''
})

/** When sidebar is collapsed AND we're on Products page, main content is edge-to-edge; else default behavior. */
const layoutCollapsedStyle = inject<{ value: boolean } | null>('layoutCollapsedStyle', null)
/** When true (e.g. Products page), main content padding changes instantly to avoid table ResizeObserver thrash during sidebar transition */
const layoutDisablePaddingTransition = inject<{ value: boolean } | null>('layoutDisablePaddingTransition', null)
const pageContentWrapperClasses = computed(() => {
  if (isMobile.value || !layoutCollapsedStyle?.value) return ''
  return 'px-0 layout-sidebar-collapsed'
})

const mainContentStyles = computed(() => {
  const base: Record<string, string> = {}
  // Only reserve space for fixed bottom bar when it is visible; otherwise no bottom padding so content fits viewport
  base.paddingBottom = bottomPaginationVisible.value ? 'var(--bottom-bar-height)' : '0'
  if (isMobile.value || !props.sidebarWidth) {
    base['--bottom-bar-inset-left'] = '0'
    return Object.keys(base).length ? base : undefined
  }
  const width = isCollapsed.value
    ? Number(props.collapsedWidth) || 64
    : Number(props.sidebarWidth) || 256
  const disableTransition = !!layoutDisablePaddingTransition?.value
  return {
    ...base,
    paddingLeft: `${width}px`,
    '--bottom-bar-inset-left': `${width}px`,
    ...(disableTransition
      ? { transitionDuration: '0s' }
      : {
          transitionProperty: 'padding-left',
          transitionDuration: '0.15s',
          transitionTimingFunction: 'ease-out'
        })
  }
})

// Methods
const toggleSidebar = () => {
  if (isMobile.value) {
    isOpen.value = !isOpen.value
    emit('update:modelValue', isOpen.value)
    emit('toggle')
  } else {
    isCollapsed.value = !isCollapsed.value
    emit('collapse-change', isCollapsed.value)
    emit('toggle')
  }
}

// So top bar hamburger uses this (mobile: drawer, desktop: collapse) instead of provider's desktop-only toggle
provide('layoutSidebarToggle', toggleSidebar)

const closeSidebar = () => {
  if (isMobile.value) {
    isOpen.value = false
    emit('update:modelValue', false)
  }
}

const handleResize = () => {
  const breakpointMap: Record<string, number> = {
    md: 768,
    lg: 1024,
    xl: 1280
  }
  
  const breakpoint = breakpointMap[props.breakpoint] || 1024
  const wasMobile = isMobile.value
  isMobile.value = window.innerWidth < breakpoint

  // Auto-close mobile sidebar when switching to desktop
  if (!isMobile.value && isOpen.value) {
    isOpen.value = false
    emit('update:modelValue', false)
  }
  
  // If we just switched from desktop to mobile, ensure sidebar is closed initially
  if (isMobile.value && !wasMobile && !props.modelValue) {
    isOpen.value = false
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  // Clear shift-just-released on any key so normal focus-visible behavior resumes (e.g. Tab shows ring on new focus).
  document.body.classList.remove('shift-just-released')
  if (event.key === 'Escape' && isMobile.value && isOpen.value) {
    closeSidebar()
  }
}

function onShiftKeydown(e: KeyboardEvent) {
  if (e.key === 'Shift') document.body.classList.add('shift-key-held')
}

function onShiftKeyup(e: KeyboardEvent) {
  if (e.key === 'Shift') {
    document.body.classList.remove('shift-key-held')
    // Shift alone must never reveal focus/active styles. Keep suppressing until next key or mouse action.
    document.body.classList.add('shift-just-released')
  }
}

function onMousedownClearShiftReleased() {
  document.body.classList.remove('shift-just-released')
}

const saveState = () => {
  if (props.persistState) {
    // Only persist mobile drawer open state; collapsed is owned by parent (layout.sidebarCollapsed)
    const state = { open: isOpen.value }
    localStorage.setItem(props.storageKey, JSON.stringify(state))
  }
}

const loadState = () => {
  if (props.persistState) {
    try {
      const saved = localStorage.getItem(props.storageKey)
      if (saved) {
        const state = JSON.parse(saved)
        isOpen.value = state.open ?? props.modelValue
        // collapsed is driven by parent prop only, not restored here
      }
    } catch (error) {
      console.warn('Failed to load layout state:', error)
    }
  }
}

// Body scroll lock for mobile overlay
const lockBodyScroll = () => {
  if (isMobile.value && isOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

// Watchers
watch(() => props.modelValue, (newValue: boolean) => {
  isOpen.value = newValue
})

watch(
  () => route.fullPath,
  () => {
    if (isMobile.value && isOpen.value) {
      closeSidebar()
    }
  }
)

watch(() => props.collapsed, (newValue: boolean) => {
  isCollapsed.value = newValue
})

watch(isOpen, () => {
  saveState()
})

watch([isMobile, isOpen], () => {
  lockBodyScroll()
})

// Lifecycle
onMounted(() => {
  handleResize() // Check mobile state first
  loadState() // Then load saved state
  // Ensure mobile sidebar starts closed if not explicitly set
  if (isMobile.value && !props.modelValue) {
    isOpen.value = false
  }
  window.addEventListener('resize', handleResize)
  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('keydown', onShiftKeydown)
  document.addEventListener('keyup', onShiftKeyup)
  document.addEventListener('mousedown', onMousedownClearShiftReleased)
})


onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('keydown', onShiftKeydown)
  document.removeEventListener('keyup', onShiftKeyup)
  document.removeEventListener('mousedown', onMousedownClearShiftReleased)
  document.body.classList.remove('shift-key-held', 'shift-just-released')
  // Ensure body scroll is unlocked
  document.body.style.overflow = ''
})

// Expose methods for parent components
defineExpose({
  toggleSidebar,
  closeSidebar,
  isOpen: computed(() => isOpen.value),
  isCollapsed: computed(() => isCollapsed.value),
  isMobile: computed(() => isMobile.value)
})
</script>

<style scoped>
/* Sidebar is now fixed at 320px width on all screens */
/* Main content padding is handled by lg:pl-[320px] Tailwind class */

/* Force page root to use full width (overrides per-view max-w-7xl and other max-width utilities) */
:deep(.page-content-full-width > *) {
  max-width: none !important;
  width: 100% !important;
}

/* Many views put max-w-7xl on an inner div (root is a wrapper). Override that container so content uses full width. */
:deep(.page-content-full-width .max-w-7xl) {
  max-width: none !important;
  width: 100% !important;
}

/* When sidebar is collapsed, zero horizontal padding on slot root so content is edge-to-edge without patching views. */
:deep(.layout-sidebar-collapsed > *) {
  padding-left: 0;
  padding-right: 0;
}

/* When sidebar is collapsed, main content container (max-w-7xl div with sticky search + filters) gets p-0. */
:deep(.layout-sidebar-collapsed .max-w-7xl) {
  padding: 0;
}
</style>

<style>
/* Suppress focus/active visuals while Shift is held or just released (e.g. Shift+wheel scroll).
   Prevents releasing Shift from revealing focus ring on the last interacted element.
   Cleared on next keydown or mousedown so Tab/real keyboard nav still shows focus ring. */
body.shift-key-held *:focus,
body.shift-key-held *:focus-visible,
body.shift-just-released *:focus,
body.shift-just-released *:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}
</style>
